"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Chip,
  DatePicker,
  type DateValue,
  Image,
  Input,
  Progress,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Tooltip,
} from "@heroui/react";
import { I18nProvider, useDateFormatter, useLocale } from "@react-aria/i18n";
import { useMediaQuery } from "usehooks-ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  getLocalTimeZone,
  parseDate,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { Form } from "../../components/ui/form";
import { registeringAnimal } from "../Animals/animalApiSlice";
import useAuth from "../../hooks/useAuth";
import { animalSchema, type TAnimalSchema } from "@/models/Animal.Schema.ts";
import { AuthStore } from "@/store/useAuthStore.ts";
import {
  birthConditions,
  breedingTypes,
  coatColor,
  frameSizes,
  nigerianBreeds,
  paternalTypes,
  ratios,
  statuses,
  vaccineArray,
} from "@/utils/items.ts";
import { cn } from "@/lib/utils.ts";
import { storage } from "@/config/firebase.ts";
import { ensureStorageAuthenticated } from "@/config/storageAuth";
import { useNavigate } from "react-router-dom";

const FarmerAnimalsForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [img, setImg] = useState<File>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<string | null>(null);
  const [value, setValue] = useState<DateValue | null>(parseDate("2024-04-04"));
  const isMobile = useMediaQuery("(max-width: 768px)");
  const queryClient = useQueryClient();
  const userAuth = useAuth();
  const id = userAuth?.id;
  const { farmInfo } = AuthStore();

  const FORM_STORAGE_KEY = "farmerAnimalFormData";

  const { mutateAsync, isError, failureCount, status } = useMutation({
    mutationKey: ["registerAnimals"],
    mutationFn: async (data) => {
      return await registeringAnimal(data);
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["animals", id] });
      const optimisticAnimal = {
        ...data,
        _id: `optimistic-${Date.now()}`,
      };
      const previousAnimals = queryClient.getQueryData<any[]>(["animals"]);
      queryClient.setQueryData<any>(["animals", id], (old: any) => {
        if (!old) return [];
        return old ? [...old, data] : [optimisticAnimal];
      });
      return { previousAnimals };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["animals", id] });
      toast.success("New Animal has been registered successfully");
      localStorage.removeItem(FORM_STORAGE_KEY);
      form.reset();
      setPreview(null);
      setImg(undefined);
      setCurrentStep(0);
      navigate("/overview");
    },
    onError: useCallback(
      (error: Error, context: any) => {
        if (error instanceof AxiosError) {
          toast.error(`${error.response?.data.message}`);
        }
        toast.error("Failed to Register New Animal. Reverting...");
        queryClient.setQueryData(["animals", id], context?.previousAnimals);
      },
      [queryClient],
    ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["animals", id] });
    },
  });

  const handleImageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImg(file);
        setPreview(URL.createObjectURL(file));
      }
    },
    [setImg, setPreview],
  );

  const form = useForm<TAnimalSchema>({
    resolver: zodResolver(animalSchema),
    mode: "onChange",
    defaultValues: {
      gn: "",
      sex: "",
      name: "",
      weight: "",
      vaccination: "",
      damDGR: "",
      damBreed: "",
      damName: "",
      sireDGR: "",
      sireName: "",
      sireBreed: "",
      age: "",
      paternalGrandDamDGR: "",
      paternalGrandDamName: "",
      paternalGrandDamBreed: "",
      paternalGrandSireDGR: "",
      paternalGrandSireName: "",
      paternalGrandSireBreed: "",
      maternalGrandDamDGR: "",
      maternalGrandDamName: "",
      maternalGrandDamBreed: "",
      maternalGrandSireDGR: "",
      maternalGrandSireName: "",
      maternalGrandSireBreed: "",
      animalStatus: "",
      birthWeight: "",
      coatColor: "",
      size: "",
      birthCondition: "",
      breedingType: "",
      paternalType: "",
      racialComposition: "",
      breedCompositionRatio: "",
    },
  });

  const steps = [
    {
      id: "Step 1",
      name: "Animal Information",
      description: "Basic animal details and characteristics",
      fields: [
        "gn",
        "name",
        "birthDate",
        "age",
        "status",
        "sex",
        "age",
        "vaccination",
        "weight",
        "birthWeight",
        "breedingType",
        "size",
        "coatColor",
        "racialComposition",
        "breedCompositionRatio",
      ],
    },
    {
      id: "Step 2",
      name: "Family Information",
      description: "Genealogy and pedigree information",
      fields: [
        "damWeightAtWeaning",
        "damDGR",
        "damName",
        "damBreed",
        "sireDGR",
        "sireName",
        "sireBreed",
        "paternalGrandDamDGR",
        "paternalGrandDamName",
        "paternalGrandDamBreed",
        "paternalGrandSireDGR",
        "paternalGrandSireName",
        "paternalGrandSireBreed",
        "maternalGrandDamDGR",
        "maternalGrandDamName",
        "maternalGrandDamBreed",
        "maternalGrandSireDGR",
        "maternalGrandSireName",
        "maternalGrandSireBreed",
      ],
    },
    {
      id: "Step 3",
      name: "Photo & Final Details",
      description: "Upload photo and complete registration",
      fields: ["photo", "paternalType", "birthCondition"],
    },
  ];

  const { locale } = useLocale();
  const formatter = useDateFormatter({ dateStyle: "full" });
  const now = today(getLocalTimeZone());
  const nextWeek = startOfWeek(now.add({ weeks: 1 }), locale);
  const nextMonth = startOfMonth(now.add({ months: 1 }));

  const CustomRadio = (props: any) => {
    const { children, ...otherProps } = props;
    return (
      <Radio
        {...otherProps}
        classNames={{
          base: cn(
            "flex-none m-0 h-8 bg-content1 hover:bg-content2 items-center justify-between",
            "cursor-pointer rounded-full border-2 border-default-200/60",
            "data-[selected=true]:border-primary transition-all duration-200",
          ),
          label: "text-tiny text-default-500",
          labelWrapper: "px-1 m-0",
          wrapper: "hidden",
        }}
      >
        {children}
      </Radio>
    );
  };

  const onSubmit: SubmitHandler<TAnimalSchema> = async () => {
    try {
      await ensureStorageAuthenticated();

      if (!img) {
        throw new Error("No image provided.");
      }
      const sanitizedName = img.name.replace(/[^a-zA-Z0-9.]/g, "_");
      const fileName = `${Date.now()}_${sanitizedName}`;
      const storageRef = ref(storage, "images/animals/" + fileName);
      const metadata = {
        contentType: img.type,
        customMetadata: {
          farmerID: id || "",
          farmID: farmInfo.id || "",
        },
      };
      const uploadTask = uploadBytesResumable(storageRef, img, metadata);
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          toast.info("Image Uploading", {
            duration: 2000,
          });
          switch (snapshot.state) {
            case "paused":
              break;
            case "running":
              break;
          }
        },
        (error) => {
          switch (error.code) {
            case "storage/unauthorized":
              break;
            case "storage/canceled":
              break;
            case "storage/unknown":
              break;
          }
        },
        async () => {
          let url;
          await getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            url = downloadURL;
          });
          // const ageInDays = calculateAge(
          //   new Date(formatter.format(value!.toDate(getLocalTimeZone()))),
          // );

          const currentWeight = Number(form.getValues().weight) || 0;
          const birthW = Number(form.getValues().birthWeight) || 0;

          const birthDate = value!.toDate(getLocalTimeZone());
          const animalData = {
            gn:
              "SG" +
              (farmInfo?.farmName ?? "").slice(0, 2) +
              form.getValues().gn,
            // Use the validated, formatted date
            birthDate: formatter.format(birthDate),
            sex: form.getValues().sex,
            name: form.getValues().name,
            age: form.getValues().age,
            size: form.getValues().size,
            // Use the safely parsed numbers
            weight: currentWeight,
            birthWeight: birthW,
            coatColor: form.getValues().coatColor,
            breedingType: form.getValues().breedingType,
            // Use the calculated values after applying .toFixed()
            damName: form.getValues().damName,
            damDGR: form.getValues().damDGR,
            damBreed: form.getValues().damBreed,
            sireDGR: form.getValues().sireDGR,
            sireName: form.getValues().sireName,
            sireBreed: form.getValues().sireBreed,
            paternalGrandDamDGR: form.getValues().paternalGrandDamDGR,
            paternalGrandDamName: form.getValues().paternalGrandDamName,
            paternalGrandDamBreed: form.getValues().paternalGrandDamBreed,
            paternalGrandSireDGR: form.getValues().paternalGrandSireDGR,
            paternalGrandSireName: form.getValues().paternalGrandSireName,
            paternalGrandSireBreed: form.getValues().paternalGrandSireBreed,
            maternalGrandDamDGR: form.getValues().maternalGrandDamDGR,
            maternalGrandDamName: form.getValues().maternalGrandDamName,
            maternalGrandDamBreed: form.getValues().maternalGrandDamBreed,
            maternalGrandSireDGR: form.getValues().maternalGrandSireDGR,
            maternalGrandSireName: form.getValues().maternalGrandSireName,
            maternalGrandSireBreed: form.getValues().maternalGrandSireBreed,
            birthCondition: form.getValues().birthCondition,
            paternalType: form.getValues().paternalType,
            racialComposition:
              form.getValues().racialComposition +
              form.getValues().breedCompositionRatio,
            vaccination: form.getValues().vaccination,
            animalStatus: form.getValues().animalStatus,
            farmerID: id || "",
            farmID: farmInfo.id,
            url: url,
          };
          if (!img) {
            return new Error("No Image");
          }
          const formData = new FormData();
          formData.append("data", JSON.stringify(animalData));
          formData.append("photo", img);
          toast.promise(mutateAsync(animalData), {
            loading: "Registering animal...",
            success: "Animal Registered Successfully",
            position: "top-right",
          });
        },
      );
      if (isError) {
        console.log(status);
        console.log(failureCount);
      }
    } catch (error) {
      console.log(error);
    }
  };

  type FieldName = keyof TAnimalSchema;
  const next = async () => {
    const fields = steps[currentStep].fields;
    const output = await form.trigger(fields as FieldName[], {
      shouldFocus: true,
    });
    if (!output) return;
    if (currentStep < steps.length - 1) {
      if (currentStep === steps.length - 2) {
        await form.handleSubmit(onSubmit)();
      }
      setCurrentStep((step) => step + 1);
    }
  };

  const prev = () => {
    if (currentStep > 0) {
      setCurrentStep((step) => step - 1);
      form.reset();
    }
  };

  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  useEffect(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      form.reset(JSON.parse(saved));
    }
  }, [form]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(values));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8 animate-in slide-in-from-top duration-700">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            Animal Registration
          </h1>
          <p className="text-gray-600 text-lg">
            Complete the form to register a new animal in your farm
          </p>
        </div>

        {/* Progress Section */}
        <Card className="mb-8 shadow-lg border-0 bg-white/80 backdrop-blur-sm animate-in slide-in-from-top duration-700 delay-150">
          <CardBody className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Step {currentStep + 1} of {steps.length}
              </h2>
              <Chip color="primary" variant="flat" className="animate-pulse">
                {Math.round(progressPercentage)}% Complete
              </Chip>
            </div>
            <Progress
              value={progressPercentage}
              className="mb-4"
              color="primary"
              size="lg"
            />
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={cn(
                    "flex flex-col items-center transition-all duration-300",
                    index <= currentStep ? "text-primary" : "text-gray-400",
                  )}
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold mb-2 transition-all duration-300",
                      index <= currentStep
                        ? "bg-primary text-white shadow-lg scale-110"
                        : "bg-gray-200 text-gray-500",
                    )}
                  >
                    {index + 1}
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-sm">{step.name}</p>
                    {!isMobile && (
                      <p className="text-xs opacity-70">{step.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Main Form Card */}
        <Card className="shadow-2xl border-0 bg-white/90 backdrop-blur-sm animate-in slide-in-from-bottom duration-700 delay-300">
          <CardHeader className="pb-2">
            <div className="w-full text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-1">
                {steps[currentStep].name}
              </h3>
              <p className="text-gray-600">{steps[currentStep].description}</p>
            </div>
          </CardHeader>
          <CardBody className="p-8">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                {/* Step 1: Animal Information */}
                {currentStep === 0 && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    {/* Basic Information Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Basic Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="gn"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The ID of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.gn?.message
                                }
                                isInvalid={!!form.formState?.errors?.gn}
                                label="Birth Registry Id"
                                labelPlacement="outside"
                                placeholder="ID of The Animal"
                                size="lg"
                                variant="bordered"
                              />
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Name OF The Animal"
                              delay={1000}
                              offset={10}
                            >
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={`${form.formState?.errors?.name?.message}`}
                                isInvalid={!!form.formState?.errors?.name}
                                label="Name"
                                labelPlacement="outside"
                                placeholder="Name of The Animal"
                                size="lg"
                                variant="bordered"
                              />
                            </Tooltip>
                          )}
                        />
                      </div>
                    </div>

                    {/* Physical Characteristics Section */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                        Physical Characteristics
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Tooltip
                          color="secondary"
                          content="The Birth Date of The Animal"
                          delay={500}
                          offset={10}
                        >
                          <I18nProvider locale="en-US">
                            <DatePicker
                              showMonthAndYearPickers
                              CalendarBottomContent={
                                <RadioGroup
                                  aria-label="Date precision"
                                  classNames={{
                                    base: "w-full pb-2",
                                    wrapper:
                                      "-my-2.5 py-2.5 px-3 gap-1 flex-nowrap max-w-[380px] overflow-x-scroll",
                                  }}
                                  defaultValue="exact_dates"
                                  orientation="horizontal"
                                >
                                  <CustomRadio value="unknown">
                                    Unknown
                                  </CustomRadio>
                                </RadioGroup>
                              }
                              CalendarTopContent={
                                <ButtonGroup
                                  fullWidth
                                  className="px-3 pb-2 pt-3 bg-content1 [&>button]:text-default-500 [&>button]:border-default-200/60"
                                  radius="full"
                                  size="sm"
                                  variant="bordered"
                                >
                                  <Button onPress={() => setValue(now)}>
                                    Today
                                  </Button>
                                  <Button onPress={() => setValue(nextWeek)}>
                                    Next week
                                  </Button>
                                  <Button onPress={() => setValue(nextMonth)}>
                                    Next month
                                  </Button>
                                </ButtonGroup>
                              }
                              calendarProps={{
                                focusedValue: value,
                                onFocusChange: setValue,
                                nextButtonProps: {
                                  variant: "bordered",
                                },
                                prevButtonProps: {
                                  variant: "bordered",
                                },
                              }}
                              className="transition-all duration-200 hover:scale-[1.02]"
                              description={"date format mm/dd/yyyy"}
                              label="Birth date"
                              labelPlacement={"outside"}
                              size="lg"
                              value={value}
                              onChange={setValue}
                              variant="bordered"
                            />
                          </I18nProvider>
                        </Tooltip>
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="coatColor"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Color of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.coatColor?.message
                                }
                                isInvalid={!!form.formState.errors.coatColor}
                                label="Coat Color"
                                labelPlacement="outside"
                                placeholder="Coat Color of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                {coatColor.map((color) => (
                                  <SelectItem key={color.label}>
                                    {color.coatColor}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name="age"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Estimated Age Of The Animal"
                              delay={1000}
                              offset={10}
                            >
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={`${form.formState?.errors?.age?.message}`}
                                isInvalid={!!form.formState?.errors?.age}
                                label="Age"
                                labelPlacement="outside"
                                placeholder="Age of The Animal"
                                size="lg"
                                variant="bordered"
                              />
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="animalStatus"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Status / situation of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.animalStatus?.message
                                }
                                isInvalid={!!form.formState.errors.animalStatus}
                                label="Status"
                                labelPlacement="outside"
                                placeholder="Status of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                {statuses.map((stat) => (
                                  <SelectItem key={stat.uid}>
                                    {stat.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                      </div>
                    </div>

                    {/* Additional Details Section */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                        Additional Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="sex"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Sex of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.sex?.message
                                }
                                isInvalid={!!form.formState.errors.sex}
                                label="Sex"
                                labelPlacement="outside"
                                placeholder="Sex of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                <SelectItem key={"male"}>Male</SelectItem>
                                <SelectItem key={"female"}>Female</SelectItem>
                              </Select>
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name="vaccination"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Vaccine which the Animal was Taking"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.vaccination?.message
                                }
                                isInvalid={!!form.formState.errors.vaccination}
                                label="Vaccination"
                                labelPlacement="outside"
                                placeholder="Select Vaccines"
                                selectionMode="multiple"
                                size="lg"
                                variant="bordered"
                              >
                                {vaccineArray.map((vaccine) => (
                                  <SelectItem key={vaccine.value}>
                                    {vaccine.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          name="weight"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Weight of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Input
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.weight?.message
                                }
                                id="weight"
                                isInvalid={!!form.formState.errors.weight}
                                label="Weight"
                                labelPlacement="outside"
                                placeholder="Weight of The Animal"
                                size="lg"
                                type="number"
                                variant="bordered"
                              />
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue={undefined}
                          name="birthWeight"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Birth Weight of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Input
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.birthWeight?.message
                                }
                                id="birthWeight"
                                isInvalid={!!form.formState.errors.birthWeight}
                                label="Birth Weight"
                                labelPlacement="outside"
                                placeholder="Birth Weight of The Animal"
                                size="lg"
                                type="number"
                                variant="bordered"
                              />
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="breedingType"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Breeding Type of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.breedingType?.message
                                }
                                isInvalid={!!form.formState.errors.breedingType}
                                label="Breeding Type"
                                labelPlacement="outside"
                                placeholder="Breeding of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                {breedingTypes.map((b) => (
                                  <SelectItem key={b.name}>
                                    {b.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="size"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Size of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.size?.message
                                }
                                isInvalid={!!form.formState.errors.size}
                                label="Size"
                                labelPlacement="outside"
                                placeholder="Size of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                {frameSizes.map((size) => (
                                  <SelectItem key={size.value}>
                                    {size.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="racialComposition"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Breed of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.racialComposition
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState.errors.racialComposition
                                }
                                label="Breed Composition"
                                labelPlacement="outside"
                                placeholder="Breed of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="breedCompositionRatio"
                          render={({ field }) => (
                            <Tooltip
                              color="secondary"
                              content="The Breed Composition of The Animal"
                              delay={500}
                              offset={10}
                            >
                              <Select
                                {...field}
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.breedCompositionRatio
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState.errors.breedCompositionRatio
                                }
                                label="Breed Composition Ratio"
                                labelPlacement="outside"
                                placeholder=" Breed Composition of The Animal"
                                size="lg"
                                variant="bordered"
                              >
                                {ratios.map((ratio: any) => (
                                  <SelectItem key={ratio.value}>
                                    {ratio.value}
                                  </SelectItem>
                                ))}
                              </Select>
                            </Tooltip>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Family Information */}
                {currentStep === 1 && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    {/* Parents Section */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                        Parents Information
                      </h4>

                      {/* Sire Information */}
                      <div className="mb-6">
                        <h5 className="text-md font-medium text-gray-700 mb-3">
                          Sire (Father)
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            control={form.control}
                            name="sireName"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.sireName?.message
                                }
                                isInvalid={!!form.formState?.errors?.sireName}
                                label="Sire Name"
                                labelPlacement="outside"
                                placeholder="sire's Name"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            name="sireDGR"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.sireDGR?.message
                                }
                                isInvalid={!!form.formState?.errors?.sireDGR}
                                label="Sire's DGR"
                                labelPlacement="outside"
                                placeholder="Sire's DGR"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            defaultValue=""
                            name="sireBreed"
                            render={({ field }) => (
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.sireBreed?.message
                                }
                                isInvalid={!!form.formState?.errors?.sireBreed}
                                label="sire's Breed"
                                labelPlacement="outside"
                                placeholder="sire's Breed"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      </div>

                      {/* Dam Information */}
                      <div>
                        <h5 className="text-md font-medium text-gray-700 mb-3">
                          Dam (Mother)
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            control={form.control}
                            name="damName"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.damName?.message
                                }
                                isInvalid={!!form.formState?.errors?.damName}
                                label="Dam Name"
                                labelPlacement="outside"
                                placeholder="Dam's Name"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            name="damDGR"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.damDGR?.message
                                }
                                isInvalid={!!form.formState?.errors?.damDGR}
                                label="Dam DGR"
                                labelPlacement="outside"
                                placeholder="Dam DGR"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            defaultValue=""
                            name="damBreed"
                            render={({ field }) => (
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.damBreed?.message
                                }
                                isInvalid={!!form.formState?.errors?.damBreed}
                                label="Dam Breed"
                                labelPlacement="outside"
                                placeholder="Dam Breed"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Paternal Grandparents Section */}
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                        Paternal Grandparents
                      </h4>

                      {/* Paternal GrandDam */}
                      <div className="mb-6">
                        <h5 className="text-md font-medium text-gray-700 mb-3">
                          Paternal GrandDam
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            control={form.control}
                            name="paternalGrandDamName"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.paternalGrandDamName
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors?.paternalGrandDamName
                                }
                                label="Paternal GrandDam Name"
                                labelPlacement="outside"
                                placeholder="Paternal GrandDam Name"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            name="paternalGrandDamDGR"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.paternalGrandDamDGR
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors?.paternalGrandDamDGR
                                }
                                label="Paternal GrandDam DGR"
                                labelPlacement="outside"
                                placeholder="Paternal GrandDam DGR"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            defaultValue=""
                            name="paternalGrandDamBreed"
                            render={({ field }) => (
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.paternalGrandDamBreed
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors
                                    ?.paternalGrandDamBreed
                                }
                                label="Paternal GrandDam Breed"
                                labelPlacement="outside"
                                placeholder="Paternal GrandDam Breed"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      </div>

                      {/* Paternal GrandSire */}
                      <div>
                        <h5 className="text-md font-medium text-gray-700 mb-3">
                          Paternal GrandSire
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            control={form.control}
                            name="paternalGrandSireName"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.paternalGrandSireName
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors
                                    ?.paternalGrandSireName
                                }
                                label="Paternal GrandSire Name"
                                labelPlacement="outside"
                                placeholder="Paternal GrandSire Name"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            name="paternalGrandSireDGR"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.paternalGrandSireDGR
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors?.paternalGrandSireDGR
                                }
                                label="Paternal GrandSire DGR"
                                labelPlacement="outside"
                                placeholder="Paternal GrandSire DGR"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            defaultValue=""
                            name="paternalGrandSireBreed"
                            render={({ field }) => (
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.paternalGrandSireBreed
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors
                                    ?.paternalGrandSireBreed
                                }
                                label="Paternal GrandSire Breed"
                                labelPlacement="outside"
                                placeholder="Paternal GrandSire Breed"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Maternal Grandparents Section */}
                    <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border border-rose-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-rose-500 rounded-full mr-3"></div>
                        Maternal Grandparents
                      </h4>

                      {/* Maternal GrandSire */}
                      <div className="mb-6">
                        <h5 className="text-md font-medium text-gray-700 mb-3">
                          Maternal GrandSire
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            control={form.control}
                            name="maternalGrandSireName"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.maternalGrandSireName
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors
                                    ?.maternalGrandSireName
                                }
                                label="Maternal Grand Sire Name"
                                labelPlacement="outside"
                                placeholder="Maternal Grand Sire Name"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            name="maternalGrandSireDGR"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.maternalGrandSireDGR
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors?.maternalGrandSireDGR
                                }
                                label="Maternal GrandSire DGR"
                                labelPlacement="outside"
                                placeholder="Maternal GrandSire DGR"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            defaultValue=""
                            name="maternalGrandSireBreed"
                            render={({ field }) => (
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.maternalGrandSireBreed
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors
                                    ?.maternalGrandSireBreed
                                }
                                label="Dam's Father Breed"
                                labelPlacement="outside"
                                placeholder="Maternal GrandSire Breed"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      </div>

                      {/* Maternal GrandDam */}
                      <div>
                        <h5 className="text-md font-medium text-gray-700 mb-3">
                          Maternal GrandDam
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            control={form.control}
                            name="maternalGrandDamName"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.maternalGrandDamName
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors?.maternalGrandDamName
                                }
                                label="Maternal GrandDam Name"
                                labelPlacement="outside"
                                placeholder="Maternal GrandDam Name"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            name="maternalGrandDamDGR"
                            render={({ field }) => (
                              <Input
                                {...field}
                                isClearable
                                isRequired
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.maternalGrandDamDGR
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors?.maternalGrandDamDGR
                                }
                                label="Maternal GrandDam DGR"
                                labelPlacement="outside"
                                placeholder="Maternal GrandDam DGR"
                                size="lg"
                                variant="bordered"
                              />
                            )}
                          />
                          <Controller
                            control={form.control}
                            defaultValue=""
                            name="maternalGrandDamBreed"
                            render={({ field }) => (
                              <Select
                                {...field}
                                className="transition-all duration-200 hover:scale-[1.02]"
                                errorMessage={
                                  form.formState?.errors?.maternalGrandDamBreed
                                    ?.message
                                }
                                isInvalid={
                                  !!form.formState?.errors
                                    ?.maternalGrandDamBreed
                                }
                                label="Maternal GrandDam Breed"
                                labelPlacement="outside"
                                placeholder="Maternal GrandDam Breed"
                                size="lg"
                                variant="bordered"
                              >
                                {nigerianBreeds.map((breed) => (
                                  <SelectItem key={breed.breed}>
                                    {breed.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Photo & Final Details */}
                {currentStep === 2 && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    {/* Final Details Section */}
                    <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-violet-500 rounded-full mr-3"></div>
                        Final Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="paternalType"
                          render={({ field }) => (
                            <Select
                              {...field}
                              className="transition-all duration-200 hover:scale-[1.02]"
                              errorMessage={
                                form.formState?.errors?.paternalType?.message
                              }
                              isInvalid={!!form.formState?.errors?.paternalType}
                              label="Paternal Type"
                              labelPlacement="outside"
                              placeholder="Paternal Type"
                              size="lg"
                              variant="bordered"
                            >
                              {paternalTypes.map((pattern) => (
                                <SelectItem key={pattern.value}>
                                  {pattern.label}
                                </SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        <Controller
                          control={form.control}
                          defaultValue=""
                          name="birthCondition"
                          render={({ field }) => (
                            <Select
                              {...field}
                              className="transition-all duration-200 hover:scale-[1.02]"
                              errorMessage={
                                form.formState?.errors?.birthCondition?.message
                              }
                              isInvalid={
                                !!form.formState?.errors?.birthCondition
                              }
                              label="Birth Condition"
                              labelPlacement="outside"
                              placeholder="Birth Condition"
                              size="lg"
                              variant="bordered"
                            >
                              {birthConditions.map((pattern) => (
                                <SelectItem key={pattern.uid}>
                                  {pattern.label}
                                </SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                    </div>

                    {/* Photo Upload Section */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-8 border border-emerald-100 text-center">
                      <h4 className="text-lg font-semibold text-gray-800 mb-6 flex items-center justify-center">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                        Animal Photo
                      </h4>

                      {preview && (
                        <div className="mb-6 flex justify-center">
                          <div className="relative group">
                            <Image
                              isBlurred
                              isZoomed
                              alt="Animal Album Cover"
                              className="shadow-2xl shadow-emerald-700/30 transition-all duration-300 group-hover:shadow-emerald-700/50"
                              height={300}
                              loading="lazy"
                              shadow="lg"
                              src={preview || "/placeholder.svg"}
                              width={300}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-4">
                        <p className="text-gray-600 font-medium">
                          Upload Animal's Image With Branded ID
                        </p>
                        <div className="relative">
                          <Input
                            isClearable
                            isRequired
                            className="transition-all duration-200 hover:scale-[1.02]"
                            labelPlacement="outside"
                            placeholder="Choose animal photo"
                            size="lg"
                            type="file"
                            variant="bordered"
                            onChange={handleImageChange}
                          />
                        </div>
                        {!preview && (
                          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 bg-gray-50/50 transition-all duration-200 hover:border-emerald-400 hover:bg-emerald-50/50">
                            <div className="text-gray-500">
                              <svg
                                className="mx-auto h-12 w-12 mb-4"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                              >
                                <path
                                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <p className="text-sm">
                                Click to upload or drag and drop
                              </p>
                              <p className="text-xs text-gray-400">
                                PNG, JPG, GIF up to 10MB
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center pt-8 border-t border-gray-200">
                  <Button
                    color="secondary"
                    isDisabled={currentStep === 0}
                    size="lg"
                    variant="ghost"
                    className="transition-all duration-200 hover:scale-105"
                    onClick={prev}
                  >
                    Previous
                  </Button>

                  <div className="flex items-center space-x-2">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all duration-300",
                          index <= currentStep ? "bg-primary" : "bg-gray-300",
                        )}
                      />
                    ))}
                  </div>

                  {currentStep < steps.length - 1 ? (
                    <Button
                      color="primary"
                      size="lg"
                      className="transition-all duration-200 hover:scale-105 bg-gradient-to-r from-blue-600 to-indigo-600"
                      onPress={next}
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      color="success"
                      size="lg"
                      type="submit"
                      className="transition-all duration-200 hover:scale-105 bg-gradient-to-r from-green-600 to-emerald-600"
                    >
                      Complete Registration
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default FarmerAnimalsForm;
